import React from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import type { EstimateFull } from '../../services/estimateService';
import { buildEasyEstimateTesTableRows, type PrzedmiarTableEntry } from './EasyEstimatePrzedmiarTab';

export type EasyEstimateTesTabProps = {
  estimate: EstimateFull;
  przedmiarEntries: PrzedmiarTableEntry[];
  cellOverrides: Record<string, string>;
  /** Rozwiązane wartości „Cena jedn.” (formuły `=F4` itp.) — sumowanie netto działów musi widzieć liczbę, nie tekst formuły. */
  resolvedCenaJednByEntryId?: Map<string, string>;
  /** Suma netto wszystkich pozycji kosztorysu (jak w dolnym pasku); nie suma wierszy TES (WBS bez podwójnego liczenia). */
  sumaNettoPozycji: number;
};

function roundMoney2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatPl2(n: number): string {
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const EasyEstimateTesTab: React.FC<EasyEstimateTesTabProps> = ({
  estimate,
  przedmiarEntries,
  cellOverrides,
  resolvedCenaJednByEntryId,
  sumaNettoPozycji,
}) => {
  const rows = React.useMemo(
    () => buildEasyEstimateTesTableRows(przedmiarEntries, cellOverrides, resolvedCenaJednByEntryId),
    [przedmiarEntries, cellOverrides, resolvedCenaJednByEntryId]
  );
  const vatPct = Number(estimate.vat);
  const net = Number.isFinite(sumaNettoPozycji) ? sumaNettoPozycji : 0;
  const vatKwota =
    Number.isFinite(vatPct) && vatPct >= 0 ? roundMoney2((net * vatPct) / 100) : 0;
  const brutto = roundMoney2(net + vatKwota);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        px: 2,
        py: 2,
        gap: 1.5,
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          fontWeight: 600,
          color: '#020242',
          flexShrink: 0,
        }}
      >
        TES — tabela elementów scalonych
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderColor: '#dadce0',
          borderRadius: '4px',
          bgcolor: '#fff',
        }}
      >
        <Table stickyHeader size="small" aria-label="TES — zestawienie działów i poddziałów">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  color: '#020242',
                  bgcolor: '#f1f3f4',
                  width: 72,
                }}
              >
                Lp.
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  color: '#020242',
                  bgcolor: '#f1f3f4',
                }}
              >
                Nazwa działu
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  color: '#020242',
                  bgcolor: '#f1f3f4',
                  width: 120,
                }}
              >
                Pozycje
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  color: '#020242',
                  bgcolor: '#f1f3f4',
                  width: 132,
                }}
              >
                Netto
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 3, color: '#5F6368', fontSize: '13px' }}>
                  Brak wierszy działu w przedmiarze. Po dodaniu lub imporcie nagłówków działów zestawienie pojawi się w
                  tej tabeli.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.dzialEntryId}
                  hover={false}
                  sx={{
                    bgcolor: r.rowBg,
                    color: r.rowTextPrimary,
                    fontWeight: r.isParentDzial ? 600 : 400,
                    transition: 'filter 0.12s ease',
                    cursor: 'default',
                    '&:hover': {
                      bgcolor: r.rowBg,
                      color: r.rowTextPrimary,
                      filter: 'brightness(0.97)',
                    },
                  }}
                >
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '13px',
                      color: 'inherit',
                      verticalAlign: 'top',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                    }}
                  >
                    {r.lpWyswietlane}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '13px',
                      color: 'inherit',
                      verticalAlign: 'top',
                      wordBreak: 'break-word',
                    }}
                  >
                    {r.nazwaDzialu}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '13px',
                      color: 'inherit',
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.pozycjeLpZakres}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '13px',
                      color: 'inherit',
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatPl2(r.netto)} zł
                  </TableCell>
                </TableRow>
              ))
            )}
            <TableRow
              sx={{
                '& td': {
                  borderTop: '2px solid #dadce0',
                  bgcolor: '#fafafa',
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  color: '#020242',
                  py: 1.25,
                },
              }}
            >
              <TableCell colSpan={2}>Podsumowanie</TableCell>
              <TableCell>Suma netto</TableCell>
              <TableCell align="right">{formatPl2(net)} zł</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& td': {
                  bgcolor: '#fafafa',
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  color: '#020242',
                },
              }}
            >
              <TableCell colSpan={2} />
              <TableCell>
                VAT{Number.isFinite(vatPct) ? ` (${formatPl2(vatPct)}%)` : ''}
              </TableCell>
              <TableCell align="right">{formatPl2(vatKwota)} zł</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& td': {
                  bgcolor: '#fafafa',
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  color: '#020242',
                  pb: 1.25,
                },
              }}
            >
              <TableCell colSpan={2} />
              <TableCell>Brutto</TableCell>
              <TableCell align="right">{formatPl2(brutto)} zł</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EasyEstimateTesTab;
